/**
 * ActanonEdu Landing Page — Pain-first conversion engine.
 *
 * Routes:
 *   /            → this page (unauthenticated)
 *   /login?tab=user → individual teacher login (User Code tab)
 *   /signup      → existing signup
 *
 * Tracking events fire to window.dataLayer (no-ops if no analytics):
 *   LANDING_PAGE_VIEW · TEACHER_SIGNUP_CLICK · PILOT_WHATSAPP_CLICK ·
 *   FOUNDER_FEEDBACK_CLICK · VIEW_TERMS_CLICK · SCROLL_DEPTH (25/50/75/100).
 *
 * WhatsApp number: 91 8796869006 (Founder). Co-founder option removed per
 * product decision — single founder line keeps schools talking to a real
 * decision maker, not a sales funnel.
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, BookOpen, Brain, CheckCircle2, ClipboardCheck,
  ClipboardList, Clock, FileWarning, FlaskConical, GraduationCap, Layers, MessageCircle,
  PlayCircle, Presentation, Rocket, ShieldCheck, Sparkles, Target, Timer,
  Users, X, Zap, BarChart3, Languages, Menu,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PromoBanner, PromoBadge, PromoTrust, usePromoCampaign } from '../components/PromoCampaign';
import { trackPromoCtaClicked } from '../lib/analytics';

// ──────────── Tracking ────────────
const track = (event, payload = {}) => {
  try {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ts: Date.now(), ...payload });
  } catch { /* no-op */ }
};

// ──────────── WhatsApp ────────────
// wa.me requires the country code prefix — Indian mobile (10-digit) → 91…
const WHATSAPP_NUMBER = '918796869006';
const waLink = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
const PILOT_MESSAGE      = 'Hi, I want to bring ActanonEdu to my school via the 30-day pilot program. Please share details.';
const FOUNDER_MESSAGE    = 'Hi Founder! I would like to discuss the ActanonEdu pilot for my school.';
const TEACHER_FEEDBACK   = 'Hi Founder! I am a teacher and would like to share feedback / questions about ActanonEdu.';

// ──────────── Pilot terms ────────────
const PILOT_TERMS = [
  { title: 'Duration',                items: ['Pilot access is strictly limited to 30 days.', 'Starts from the onboarding/activation date.', 'Non-extendable unless approved.'] },
  { title: 'Enrollment',              items: ['Applications open till 30th June.', 'Duration remains 30 days regardless of signup date.'] },
  { title: 'Access',                  items: ['Teacher tools + limited ERP features.', 'Features may evolve during the pilot.'] },
  { title: 'Usage',                   items: ['Fair usage applies.', 'Abuse may lead to access restriction.'] },
  { title: 'Feedback',                items: ['Schools agree to provide feedback during the pilot.'] },
  { title: 'Data',                    items: ['Data is handled securely.', 'Used only in anonymized form for product insights.'] },
  { title: 'No Continuity Guarantee', items: ['Pilot does not guarantee free access after 30 days.'] },
  { title: 'Conversion',              items: ['Option to upgrade to a paid plan post-pilot.'] },
  { title: 'Termination',             items: ['Platform may revoke access if misuse is detected.'] },
  { title: 'Acceptance',              items: ['Participation in the pilot = agreement to these terms.'] },
];

// ──────────── Shared legal/modal shell ────────────
// Single source of truth for the dark-backdrop, sticky-header, scroll-body,
// mobile-bottom-sheet shell used by every legal popup on the landing page.
// Adding a 4th modal (e.g. Refund Policy, DPA) is now a 10-line wrapper.
function LegalModal({ open, onClose, title, subtitle, icon: Icon = ShieldCheck, testid, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-slate-950/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4 animate-in fade-in" onClick={onClose} data-testid={testid}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-5 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-xl font-bold flex items-center gap-2"><Icon className="h-5 w-5 text-indigo-600" /> {title}</h3>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid={`${testid}-close`}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-4 text-[13px] leading-relaxed text-foreground/85">
          {children}
        </div>
      </div>
    </div>
  );
}

// ──────────── Pilot Terms modal ────────────
function PilotTermsModal({ open, onClose }) {
  return (
    <LegalModal
      open={open}
      onClose={onClose}
      title="ACTANONEDU Pilot Program — Terms"
      subtitle="Read carefully before applying."
      testid="pilot-terms-modal"
    >
      {PILOT_TERMS.map((sec, i) => (
        <div key={i} className="border-l-2 border-indigo-300 pl-4">
          <div className="font-semibold text-sm mb-1">{i + 1}. {sec.title}</div>
          <ul className="space-y-1 text-sm text-foreground/80">
            {sec.items.map((it, j) => (<li key={j} className="flex gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-500 shrink-0" /><span>{it}</span></li>))}
          </ul>
        </div>
      ))}
      <div className="text-xs text-muted-foreground pt-2 border-t border-slate-200 dark:border-slate-800">Questions? Reach out via WhatsApp to confirm specifics for your school.</div>
    </LegalModal>
  );
}

// ──────────── Legal Disclaimer modal ────────────
function LegalDisclaimerModal({ open, onClose }) {
  return (
    <LegalModal
      open={open}
      onClose={onClose}
      title="Legal Disclaimer"
      subtitle="Third-Party Educational Content"
      testid="legal-disclaimer-modal"
    >
      <p>
        ActanonEdu is an independent educational technology platform and is{' '}
        <strong>not affiliated with, endorsed by, sponsored by, or officially associated with</strong>{' '}
        Project Gutenberg, NCERT (National Council of Educational Research and Training), or any other
        third-party educational repository, publisher, institution, or governing authority unless
        expressly stated otherwise.
      </p>
      <p>
        All books, publications, documents, and educational resources accessible through external public
        repositories remain the <strong>exclusive intellectual property of their respective copyright
        holders</strong>, licensors, publishers, institutions, or governing organizations. ActanonEdu
        neither claims ownership nor asserts any proprietary rights over such third-party content.
      </p>
      <p>
        Where applicable, access to publicly available resources is facilitated solely for educational,
        informational, and reference purposes in accordance with the respective platform policies, public
        availability terms, and applicable laws. Users are solely responsible for ensuring their own
        compliance with the terms of use, copyright policies, licensing conditions, and legal requirements
        governing such external resources.
      </p>
      <p>
        <strong>Access to publicly available educational content from third-party repositories shall remain
        free of charge for all users of the platform, irrespective of subscription status.</strong>{' '}
        Any fees charged by ActanonEdu are strictly limited to the platform's proprietary services and
        technology features, including but not limited to AI-assisted content generation, workflow
        automation, personalization systems, analytics, productivity tools, and other value-added digital
        services developed and maintained independently by ActanonEdu.
      </p>
      <p>
        ActanonEdu does not sell, license, redistribute, or commercially exploit third-party public
        educational content as its own intellectual property. All trademarks, logos, names, and referenced
        entities remain the property of their respective owners.
      </p>
      <div className="text-xs text-muted-foreground pt-3 border-t border-slate-200 dark:border-slate-800">
        Questions or notices regarding this disclaimer? Reach out at{' '}
        <a href="mailto:legal@actanonedu.com" className="text-indigo-700 dark:text-indigo-400 underline hover:text-indigo-900">legal@actanonedu.com</a>{' '}
        — we respond within 5 business days.
      </div>
    </LegalModal>
  );
}

// ──────────── Notice & Takedown Process modal ────────────
// Standard IP / copyright takedown SOP — what legal counsels look for when
// auditing an India-jurisdiction EdTech platform's IP posture.
function NoticeTakedownModal({ open, onClose }) {
  return (
    <LegalModal
      open={open}
      onClose={onClose}
      title="Notice & Takedown Process"
      subtitle="For copyright holders, publishers, and rights-management organisations"
      testid="notice-takedown-modal"
    >
      <p>
        ActanonEdu respects intellectual property rights and acts promptly on any legitimate notice
        concerning content accessed or surfaced through our platform. If you believe that third-party
        material made accessible via ActanonEdu infringes a copyright, trademark, or other right you
        own or represent, please submit a notice using the process below.
      </p>

      <div className="border-l-2 border-indigo-300 pl-4 space-y-2">
        <div className="font-semibold text-sm">1. Where to send</div>
        <p className="text-sm">
          Email <a href="mailto:legal@actanonedu.com" className="text-indigo-700 dark:text-indigo-400 underline">legal@actanonedu.com</a>{' '}
          with the subject line:{' '}
          <span className="font-mono bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-[11px]">Notice &amp; Takedown — [Brief Title]</span>
        </p>
      </div>

      <div className="border-l-2 border-indigo-300 pl-4 space-y-2">
        <div className="font-semibold text-sm">2. What to include</div>
        <ul className="space-y-1 text-sm text-foreground/80 list-none">
          {[
            'Identification of the work or material you allege is infringing (title, edition, ISBN/URL if available).',
            'The exact location on ActanonEdu where the alleged infringement appears (URL, page, screenshot).',
            'A statement of your good-faith belief that the use is unauthorised by the rights-holder, agent, or applicable law.',
            'Contact details — full legal name, designation, organisation, email, phone.',
            'Evidence of your authority to act (e.g. ownership certificate, power of attorney from the rights-holder, or formal designation as the rights-management organisation).',
            'A signed declaration (electronic signature accepted) that the information provided is accurate to the best of your knowledge.',
          ].map((it, i) => (
            <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-500 shrink-0" /><span>{it}</span></li>
          ))}
        </ul>
      </div>

      <div className="border-l-2 border-indigo-300 pl-4 space-y-2">
        <div className="font-semibold text-sm">3. Our response</div>
        <ul className="space-y-1 text-sm text-foreground/80 list-none">
          <li className="flex gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-500 shrink-0" /><span>We acknowledge receipt within <strong>5 business days</strong>.</span></li>
          <li className="flex gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-500 shrink-0" /><span>We complete an initial review within <strong>10 business days</strong> and act on substantiated claims by removing, restricting, or geo-fencing the affected content as appropriate.</span></li>
          <li className="flex gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-indigo-500 shrink-0" /><span>Where a notice is incomplete, we'll request the missing details within the same 10-day window.</span></li>
        </ul>
      </div>

      <div className="border-l-2 border-amber-300 pl-4 space-y-2">
        <div className="font-semibold text-sm">4. Counter-notice</div>
        <p className="text-sm">
          If you are an ActanonEdu user whose content has been removed in error, you may submit a counter-notice
          to the same address within 30 days, with evidence of your right to use the material. We will review and,
          if appropriate, restore access — and notify the original complainant accordingly.
        </p>
      </div>

      <div className="border-l-2 border-rose-300 pl-4 space-y-2">
        <div className="font-semibold text-sm">5. Misuse</div>
        <p className="text-sm">
          Submission of materially false, misleading, or vexatious notices may result in liability under
          applicable law. Repeated abuse will result in the sender being blocked.
        </p>
      </div>

      <div className="text-xs text-muted-foreground pt-3 border-t border-slate-200 dark:border-slate-800 space-y-1">
        <div>
          <strong className="text-foreground">Grievance Officer, Actanon Technologies</strong> ·{' '}
          <a href="mailto:legal@actanonedu.com" className="text-indigo-700 dark:text-indigo-400 underline">legal@actanonedu.com</a>
        </div>
        <div>Acknowledgment within 5 business days · Substantive response within 10 business days.</div>
        <div className="pt-1">
          Public monthly compliance summaries:{' '}
          <a href="/legal/transparency" className="text-indigo-700 dark:text-indigo-400 underline">/legal/transparency</a>
        </div>
      </div>
    </LegalModal>
  );
}

// ──────────── Pain-point clusters (the 20 pain points, condensed) ────────────
const PAIN_POINTS = [
  { icon: FileWarning,     title: 'Content creation overload',       desc: 'Assignments, tests, exams, worksheets — every week, every class, from scratch.' },
  { icon: Timer,           title: 'Lesson planning stress',          desc: 'Lesson plans, teaching flow, time-boxed activities — manually compiled night after night.' },
  { icon: Layers,          title: 'Curriculum alignment confusion',  desc: 'CBSE, ICSE, ICSE, IB, IGCSE, State boards — different patterns, paper formats, marking schemes.' },
  { icon: AlertTriangle,   title: 'Student engagement difficulty',   desc: 'Holding attention across diverse learning levels in a single class.' },
  { icon: ClipboardCheck,  title: 'Assessment & grading workload',   desc: 'Question papers, answer keys, reasoning explanations — repeat for every chapter.' },
  { icon: BookOpen,        title: 'Report writing & remarks',        desc: 'Crafting personalised remarks for 30+ students, term after term.' },
  { icon: Users,           title: 'Multi-class & subject chaos',     desc: 'Managing 4–6 classes × 2 subjects × 30 students = 720 touchpoints. Manually.' },
  { icon: Clock,           title: 'Time shortage & burnout',         desc: 'Teaching during the day, prepping at night, lesson plans on weekends. No personal time.' },
  { icon: Target,          title: 'No standardisation',              desc: 'Inconsistent quality between teachers; school admins can\'t enforce a quality bar.' },
  { icon: MessageCircle,   title: 'Parent communication pressure',   desc: 'Drafting parent updates, progress reports, behaviour notes — a job in itself.' },
  { icon: ShieldCheck,     title: 'Admin & documentation burden',    desc: 'Audit trails, lesson logs, compliance checklists — endless paperwork.' },
  { icon: Zap,             title: 'Last-minute exam pressure',       desc: 'Exam paper due tomorrow? Build it from scratch in 2 hours, hope for the best.' },
  { icon: BarChart3,       title: 'No centralised workflow',         desc: 'Word docs, Sheets, WhatsApp groups — your work scattered across 5 tools.' },
  { icon: Sparkles,        title: 'Quality inconsistency',           desc: 'Tired teachers produce tired content. Quality dips on bad days. Students feel it.' },
  { icon: PlayCircle,      title: 'Classroom activities scarcity',   desc: 'Debates, plays, skits, group activities — hard to design from scratch every term.' },
  { icon: Presentation,    title: 'PPT prep effort',                 desc: '40 slides per chapter × 12 chapters × 5 subjects = 2400 slides a year. Per teacher.' },
  { icon: BarChart3,       title: 'Manual performance tracking',     desc: 'Spreadsheets that nobody updates. No real visibility into how each student is doing.' },
  { icon: Brain,           title: 'Differentiated learning gap',     desc: 'Same lesson can\'t serve a topper and a struggler. Custom material is impossible at scale.' },
  { icon: ClipboardList,   title: 'Audit & compliance pressure',     desc: 'School admins demand structured documentation; teachers are not document writers.' },
  { icon: Rocket,          title: 'Repetitive manual work',          desc: 'Same task. Different chapter. Every week. No automation. No relief.' },
];

// ──────────── Page ────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const [termsOpen, setTermsOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);
  const [takedownOpen, setTakedownOpen] = useState(false);
  const scrollDepthRef = useRef({ 25: false, 50: false, 75: false, 100: false });
  // Mobile hamburger drawer state — only renders below md (768px).
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Lock body scroll when the drawer is open so the page doesn't scroll behind it.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (mobileMenuOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);
  // Live promo campaign state — drives banner, badge, button label & trust copy.
  const { active: promoActive, status: promoStatus } = usePromoCampaign();
  const signupButtonLabel = promoActive ? promoStatus.copy.signupButton : null;

  useEffect(() => {
    track('LANDING_PAGE_VIEW');
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      if (total <= 0) return;
      const pct = Math.round((h.scrollTop / total) * 100);
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !scrollDepthRef.current[m]) {
          scrollDepthRef.current[m] = true;
          track('SCROLL_DEPTH', { percent: m });
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goSignup = () => { track('TEACHER_SIGNUP_CLICK'); navigate('/signup'); };
  // Wrapped signup-CTA used by every promo-related click target so we get
  // a clean GA4 conversion path separate from organic signups.
  const goSignupPromo = (source) => {
    if (promoActive) trackPromoCtaClicked({ source });
    goSignup();
  };
  const goLogin  = () => navigate('/login?tab=user');
  const openTerms = () => { track('VIEW_TERMS_CLICK'); setTermsOpen(true); };
  const openLegal = () => { track('VIEW_LEGAL_DISCLAIMER_CLICK'); setLegalOpen(true); };
  const openTakedown = () => { track('VIEW_NOTICE_TAKEDOWN_CLICK'); setTakedownOpen(true); };
  const onWhatsApp = (label, msg = PILOT_MESSAGE) => {
    track('PILOT_WHATSAPP_CLICK', { source: label });
    window.open(waLink(msg), '_blank', 'noopener');
  };
  const onFounderFeedback = (label) => {
    track('FOUNDER_FEEDBACK_CLICK', { source: label });
    window.open(waLink(TEACHER_FEEDBACK), '_blank', 'noopener');
  };
  const scrollTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased scroll-smooth" data-testid="landing-page">
      {/* ─── PROMO BANNER (auto-hides outside campaign window) ─── */}
      <PromoBanner onCtaClick={() => goSignupPromo('promo_banner')} />

      {/* ─── HEADER ─── */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-14 sm:h-16 flex items-center justify-between gap-2">
          <button onClick={() => scrollTo('top')} className="flex items-center gap-2 sm:gap-2.5 group min-w-0" data-testid="logo">
            <img
              src="/brand/logo.png"
              alt="ActanonEdu"
              className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg object-cover shadow-md shrink-0"
            />
            <div className="text-left leading-tight min-w-0">
              <div className="font-bold tracking-tight text-sm sm:text-base">ActanonEdu</div>
              <div className="hidden sm:block text-[10px] uppercase tracking-wider text-slate-500">Student Intelligence</div>
            </div>
          </button>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <button onClick={() => scrollTo('pain')}    className="text-slate-600 hover:text-slate-900">Why ActanonEdu</button>
            <button onClick={() => scrollTo('product')} className="text-slate-600 hover:text-slate-900">See it in action</button>
            <button onClick={() => scrollTo('pilot')}   className="text-slate-600 hover:text-slate-900">School Pilot</button>
            <button onClick={() => scrollTo('founder')} className="text-slate-600 hover:text-slate-900">Talk to Founder</button>
          </nav>
          {/* ─── Desktop CTAs (≥md) ─── */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <Button variant="ghost" size="sm" onClick={goLogin} className="text-sm px-3" data-testid="header-teacher-login">
              Teacher Login
            </Button>
            <Button size="sm" onClick={goSignup} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4" data-testid="header-get-started">
              Get Started Free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          {/* ─── Mobile hamburger (<md) — 44×44 tap target ─── */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-trigger"
            className="md:hidden inline-flex items-center justify-center h-11 w-11 -mr-2 rounded-lg text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>

        {/* ─── Mobile Drawer ─── */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-50"
            data-testid="mobile-menu-drawer"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-150"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-backdrop"
            />
            <div className="absolute top-0 right-0 bottom-0 w-[88%] max-w-sm bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
              <div className="flex items-center justify-between px-4 h-14 border-b border-slate-200">
                <div className="flex items-center gap-2.5">
                  <img src="/brand/logo.png" alt="ActanonEdu" className="h-8 w-8 rounded-lg object-cover" />
                  <span className="font-bold tracking-tight text-base">ActanonEdu</span>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto py-2">
                {[
                  { label: 'Why ActanonEdu', target: 'pain' },
                  { label: 'See it in action', target: 'product' },
                  { label: 'School Pilot', target: 'pilot' },
                  { label: 'Talk to Founder', target: 'founder' },
                ].map((item) => (
                  <button
                    key={item.target}
                    type="button"
                    onClick={() => { setMobileMenuOpen(false); setTimeout(() => scrollTo(item.target), 80); }}
                    className="w-full px-5 py-3.5 text-left text-base text-slate-800 hover:bg-slate-50 active:bg-slate-100"
                    data-testid={`mobile-menu-${item.target}`}
                  >
                    {item.label}
                  </button>
                ))}
                <div className="px-5 pt-3 mt-2 border-t border-slate-200 space-y-1">
                  <button
                    type="button"
                    onClick={() => { setMobileMenuOpen(false); setLegalOpen(true); }}
                    className="block w-full py-2.5 text-left text-sm text-slate-600 hover:text-slate-900"
                    data-testid="mobile-menu-legal"
                  >
                    Legal Disclaimer
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMobileMenuOpen(false); navigate('/privacy'); }}
                    className="block w-full py-2.5 text-left text-sm text-slate-600 hover:text-slate-900"
                    data-testid="mobile-menu-privacy"
                  >
                    Privacy Policy
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMobileMenuOpen(false); navigate('/terms'); }}
                    className="block w-full py-2.5 text-left text-sm text-slate-600 hover:text-slate-900"
                    data-testid="mobile-menu-terms"
                  >
                    Terms of Service
                  </button>
                </div>
              </nav>
              {/* Sticky CTAs at bottom of drawer — primary action always reachable. */}
              <div className="border-t border-slate-200 px-4 py-3 space-y-2 bg-white">
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-11"
                  onClick={() => { setMobileMenuOpen(false); goSignup(); }}
                  data-testid="mobile-menu-signup"
                >
                  Start Free <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => { setMobileMenuOpen(false); goLogin(); }}
                  data-testid="mobile-menu-login"
                >
                  Login
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>

      <main id="top">
        {/* ─── HERO — pain hook ─── */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top,rgba(99,102,241,0.12),transparent_60%)]" />
          <div className="absolute inset-0 -z-10 [background-image:linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(to_right,rgba(15,23,42,0.04)_1px,transparent_1px)] [background-size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_75%)]" />
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-14 md:py-20 grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 gap-1.5 mb-3 sm:mb-5"><AlertTriangle className="h-3 w-3" /> Built for the modern Indian teacher</Badge>
              <h1 className="text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] sm:leading-[1.05]">
                You didn't become a teacher to spend your nights<br className="hidden sm:block" />
                <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">writing question papers.</span>
              </h1>
              <p className="mt-3 sm:mt-5 text-[15px] sm:text-lg text-slate-600 max-w-xl leading-relaxed">
                ActanonEdu generates assignments, lesson plans, questionnaires, teaching flows and slide decks in <strong className="text-slate-900">minutes — aligned to your board, your class, your chapter.</strong> So you can teach, not type.
              </p>
              <div className="mt-5 sm:mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-2.5 sm:gap-3">
                <div className="flex flex-col items-start w-full sm:w-auto">
                  <PromoBadge />
                  <Button size="lg" onClick={() => goSignupPromo('hero_cta')} className="bg-indigo-600 hover:bg-indigo-700 text-white text-base sm:text-base px-6 sm:px-7 h-12 sm:h-12 shadow-lg shadow-indigo-200 w-full sm:w-auto font-semibold" data-testid="hero-start-as-teacher">
                    {signupButtonLabel || (<>Try Free — Sign up as Teacher</>)} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                  <PromoTrust />
                </div>
                <Button size="lg" variant="outline" onClick={() => scrollTo('pilot')} className="text-sm sm:text-base h-11 sm:h-12 w-full sm:w-auto" data-testid="hero-school-pilot">
                  School Pilot Program
                </Button>
              </div>
              <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1.5 text-[11px] sm:text-xs text-slate-500">
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> No credit card</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> 30-second setup</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Free starter tier</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> All boards supported</span>
              </div>

              {/* ─── Multilingual highlight ─── */}
              <div
                data-testid="multilingual-pill"
                className="mt-3 sm:mt-5 inline-flex flex-wrap items-center gap-x-1.5 sm:gap-x-2 gap-y-1 rounded-full bg-gradient-to-r from-indigo-50 via-violet-50 to-fuchsia-50 border border-indigo-200 px-2.5 sm:px-3.5 py-1.5 sm:py-2 shadow-sm max-w-full"
              >
                <Languages className="h-3.5 w-3.5 text-indigo-600 shrink-0" />
                <span className="text-[11px] sm:text-xs font-semibold text-indigo-900">Now in</span>
                <span className="text-[11px] sm:text-xs font-medium text-slate-700">English</span>
                <span className="text-slate-300 text-[10px]">·</span>
                <span className="text-[11px] sm:text-xs font-medium text-slate-700">हिन्दी</span>
                <span className="text-slate-300 text-[10px]">·</span>
                <span className="text-[11px] sm:text-xs font-medium text-slate-700">தமிழ்</span>
                <span className="text-slate-300 text-[10px]">·</span>
                <span className="text-[11px] sm:text-xs font-medium text-slate-700">বাংলা</span>
                <span className="text-slate-300 text-[10px]">·</span>
                <span className="text-[11px] sm:text-xs font-medium text-slate-700">ਪੰਜਾਬੀ</span>
                <span className="hidden sm:inline-flex items-center text-[10px] uppercase tracking-wider font-semibold text-indigo-700 ml-1 px-1.5 py-0.5 rounded bg-indigo-100">+ more</span>
              </div>
            </div>
            {/* Real platform screenshot */}
            <div className="relative">
              <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
                <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  <span className="ml-3 text-[11px] text-slate-500 font-mono truncate">actanonedu.com — AI Teacher Content Engine</span>
                </div>
                <img
                  src="/screenshots/teaching-dashboard.png"
                  alt="ActanonEdu — AI Teacher Content Engine in action"
                  className="w-full h-auto block"
                  data-testid="hero-screenshot"
                  loading="eager"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 h-24 w-24 bg-gradient-to-br from-fuchsia-500 to-violet-600 rounded-full blur-3xl opacity-30" />
              <div className="absolute -top-5 -left-5 h-20 w-20 bg-gradient-to-br from-indigo-500 to-cyan-400 rounded-full blur-3xl opacity-30" />
            </div>
          </div>
        </section>

        {/* ─── PILOT BANNER (mandatory) ─── */}
        <section className="px-4 sm:px-8 -mt-2 mb-10">
          <div className="max-w-7xl mx-auto rounded-2xl bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-4 sm:p-7 shadow-lg shadow-amber-100/50" data-testid="pilot-banner">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4 sm:gap-5">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge className="bg-rose-600 text-white border-0 hover:bg-rose-700 text-[11px] sm:text-xs">🚀 School Pilot Program Open</Badge>
                  <Badge variant="outline" className="bg-white text-amber-800 border-amber-300 gap-1 text-[11px] sm:text-xs"><Clock className="h-3 w-3" /> Apply before <strong className="ml-1">30th June</strong></Badge>
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-slate-900">Bring ActanonEdu to your school</h3>
                <p className="text-xs sm:text-sm text-slate-700 mt-1">
                  👉 <strong>30-day pilot access</strong> · conditions apply.{' '}
                  <button onClick={openTerms} className="underline decoration-dotted underline-offset-2 hover:text-indigo-700" data-testid="banner-view-pilot-details">View Pilot Details</button>
                </p>
              </div>
              <Button onClick={() => onWhatsApp('banner-founder', FOUNDER_MESSAGE)} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full lg:w-auto" data-testid="banner-wa-founder">
                <MessageCircle className="h-4 w-4 mr-1.5" /> Chat with Founder
              </Button>
            </div>
          </div>
        </section>

        {/* ─── PAIN POINTS ─── */}
        <section id="pain" className="py-12 sm:py-16 px-4 sm:px-8 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-10">
              <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 mb-3">THE REAL TEACHER WORKLOAD</Badge>
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">If any of this sounds like your week, you're not alone.</h2>
              <p className="text-slate-600 mt-3 text-sm sm:text-base">We talked to 100+ teachers. The same 20 pain points came up again and again.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {PAIN_POINTS.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-300 hover:shadow-md transition-all" data-testid={`pain-${i}`}>
                  <div className="h-9 w-9 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center mb-2"><p.icon className="h-4 w-4" /></div>
                  <div className="font-semibold text-sm">{p.title}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-relaxed">{p.desc}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Button size="lg" onClick={goSignup} className="bg-indigo-600 hover:bg-indigo-700 text-white text-base px-8 h-12 shadow-lg shadow-indigo-200" data-testid="pain-cta">
                Stop the burnout — Try Free <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <p className="text-xs text-slate-500 mt-3">No credit card. 30-second signup. Free tier with 2 papers / 5 worksheets per month.</p>
            </div>
          </div>
        </section>

        {/* ─── SOLUTION — real platform shots ─── */}
        <section id="product" className="py-12 sm:py-16 px-4 sm:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
              <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 mb-3">HOW ACTANONEDU FIXES IT</Badge>
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">One workspace replaces every late-night scramble.</h2>
              <p className="text-slate-600 mt-3 text-sm sm:text-base">Real screenshots from inside the platform — not mockups.</p>
            </div>

            {/* Row 1 — Task type grid */}
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center mb-12 sm:mb-16">
              <div>
                <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-200 mb-3">10 TASK TYPES · 1 CLICK</Badge>
                <h3 className="text-xl sm:text-3xl font-bold tracking-tight mb-3">Assignments. Tests. Exams. Plays. Debates. Holiday Homework.</h3>
                <p className="text-slate-600 text-sm sm:text-base mb-4">Pick a task type, set the curriculum, point it at a topic — and ActanonEdu generates a complete, board-aligned document.</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  {['Section A: 10 quick task types — pick & generate', 'Section B: Plan-based — Syllabus / Exam / Concept / Activity', 'Recent Generations: one-click reuse for any class', 'Output formats: .docx · .pdf · .pptx'].map((l, i) => (<li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{l}</span></li>))}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 shadow-xl overflow-hidden bg-white">
                <img src="/screenshots/task-types-grid.png" alt="ActanonEdu task type grid — 10 generators" className="w-full h-auto block" loading="lazy" />
              </div>
            </div>

            {/* Row 2 — Live config (reverse) */}
            <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
              <div className="lg:order-2">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-3">DETERMINISTIC AI · ZERO DRIFT</Badge>
                <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">Exact marks. Exact question count. Every time.</h3>
                <p className="text-slate-600 text-base mb-4">Tell it "10 questions for 50 marks, medium difficulty, photosynthesis focus" — ActanonEdu produces exactly that. No fluff. No off-topic content.</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  {['CBSE / ICSE / IGCSE / IB / State boards', 'Difficulty mix · marks reconciliation · answer keys', 'Per-question reasoning (so you can explain it in class)', 'Optional Blueprint upload for custom paper formats'].map((l, i) => (<li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{l}</span></li>))}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 shadow-xl overflow-hidden bg-white lg:order-1">
                <img src="/screenshots/task-config.png" alt="ActanonEdu — Assignment configuration with curriculum + topic" className="w-full h-auto block" loading="lazy" />
              </div>
            </div>

            {/* Row 3 — Pricing */}
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 mb-3">FAIR PRICING · NO LOCK-IN</Badge>
                <h3 className="text-xl sm:text-3xl font-bold tracking-tight mb-3">Start free. Upgrade only when you're saving 10+ hours a month.</h3>
                <p className="text-slate-600 text-sm sm:text-base mb-4">Razorpay-powered, monthly billing, switch tiers any time.</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  {['Free: 2 question papers + 5 worksheets / month', 'Starter ₹299: unlimited worksheets & homework, no watermark', 'Pro ₹499: advanced question paper builder, smart analysis', 'Elite ₹999: bulk paper generation, syllabus planner, priority AI'].map((l, i) => (<li key={i} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" /><span>{l}</span></li>))}
                </ul>
              </div>
              <div className="rounded-2xl border border-slate-200 shadow-xl overflow-hidden bg-white">
                <img src="/screenshots/tier-pricing.png" alt="ActanonEdu pricing tiers — Starter ₹299, Pro ₹499, Elite ₹999" className="w-full h-auto block" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ─── HOW IT WORKS ─── */}
        <section className="py-12 sm:py-16 px-4 sm:px-8 bg-gradient-to-b from-indigo-50/40 to-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <Badge variant="outline" className="bg-violet-50 text-violet-800 border-violet-200 mb-3">3 STEPS</Badge>
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">From blank page to ready document in under a minute.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { n: '01', icon: Target, title: 'Choose Task',      desc: 'Assignment, Test, Exam, Summary, Lesson Plan, PPT, Debate, Skit, Holiday Homework — pick any.' },
                { n: '02', icon: Layers, title: 'Set Context',      desc: 'Board, class, subject, topic. ActanonEdu auto-aligns to your exact curriculum.' },
                { n: '03', icon: Zap,    title: 'Generate & Edit',  desc: 'Get a polished .docx / .pdf / .pptx in seconds. Edit, reuse, share with students or admin.' },
              ].map((s) => (
                <div key={s.n} className="relative rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
                  <div className="absolute -top-4 left-6 h-9 px-3 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center tracking-widest">{s.n}</div>
                  <s.icon className="h-7 w-7 text-indigo-600 mb-3" />
                  <div className="font-bold text-lg">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1.5">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── SCHOOL ERP / PILOT — heavy pull ─── */}
        <section className="py-12 sm:py-16 px-4 sm:px-8 bg-slate-900 text-white">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <Badge variant="outline" className="bg-white/10 text-white border-white/20 mb-3">FOR SCHOOL LEADERSHIP</Badge>
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">A school-wide content & ERP layer your teachers will actually use.</h2>
              <p className="text-slate-300 mt-3 sm:mt-4 max-w-lg text-sm sm:text-base">Not just AI tools. ActanonEdu unifies content generation, lesson tracking, attendance, leave, broadcasts and analytics — so your school runs on a single source of truth.</p>
              <div className="mt-5 sm:mt-6 flex flex-wrap gap-2">
                {['Content Engine', 'Attendance', 'Leave', 'Broadcasts', 'AI Insights', 'Audit-ready Reports'].map((t) => (<Badge key={t} variant="outline" className="bg-white/5 border-white/20 text-white/90 text-[11px] sm:text-xs">{t}</Badge>))}
              </div>
              <Button onClick={() => onWhatsApp('erp-section', FOUNDER_MESSAGE)} size="lg" className="mt-5 sm:mt-6 bg-emerald-500 hover:bg-emerald-600 text-white w-full sm:w-auto" data-testid="erp-apply-pilot">
                <MessageCircle className="h-4 w-4 mr-1.5" /> Apply for Pilot — Talk to Founder
              </Button>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur-sm">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: '30 days', v: 'Pilot length' },
                  { k: '5 mins',  v: 'Setup time' },
                  { k: 'Free',    v: 'During pilot' },
                  { k: 'Direct',  v: 'Founder access' },
                ].map((s) => (
                  <div key={s.k} className="rounded-xl bg-white/5 p-4 border border-white/10">
                    <div className="text-2xl font-extrabold tracking-tight">{s.k}</div>
                    <div className="text-xs text-white/60 mt-0.5">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ─── PILOT PROGRAM detail ─── */}
        <section id="pilot" className="py-12 sm:py-16 px-4 sm:px-8 bg-gradient-to-br from-indigo-50 to-violet-50">
          <div className="max-w-5xl mx-auto rounded-3xl bg-white border border-slate-200 p-6 sm:p-10 shadow-xl" data-testid="pilot-section">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <div className="text-3xl mb-2">🎯</div>
              <h2 className="text-2xl sm:text-4xl font-bold tracking-tight">Bring ActanonEdu to Your School</h2>
              <p className="text-slate-600 mt-3 text-sm sm:text-base">Limited slots. Direct founder support. Real impact in 30 days.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                { icon: Rocket,        title: 'Free for every teacher',   desc: 'Every teacher in your pilot cohort gets full access during the 30-day program.' },
                { icon: Sparkles,      title: 'Early access to features', desc: 'Be first on the roadmap. Shape the product as it ships, with priority feature requests.' },
                { icon: MessageCircle, title: 'Direct founder support',   desc: 'A WhatsApp line straight to the founder. No support tickets, no funnels.' },
              ].map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-5 bg-slate-50/50">
                  <p.icon className="h-6 w-6 text-indigo-600 mb-2" />
                  <div className="font-semibold">{p.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{p.desc}</div>
                </div>
              ))}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 mb-6 flex items-start gap-3">
              <Clock className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <strong>👉 30-day access · limited schools · conditions apply.</strong>{' '}
                <button onClick={openTerms} className="underline decoration-dotted underline-offset-2 hover:text-amber-700" data-testid="pilot-section-view-terms">Read pilot terms</button>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <Button size="lg" onClick={() => onWhatsApp('pilot-section', FOUNDER_MESSAGE)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-base px-7 h-12 shadow-lg shadow-emerald-200" data-testid="pilot-section-wa">
                <MessageCircle className="h-5 w-5 mr-1.5" /> WhatsApp the Founder
              </Button>
              <Button size="lg" variant="outline" onClick={openTerms} className="text-base h-12" data-testid="pilot-section-terms-btn">View Pilot Details</Button>
            </div>
          </div>
        </section>

        {/* ─── FOUNDER FEEDBACK CHANNEL ─── */}
        <section id="founder" className="py-12 sm:py-16 px-4 sm:px-8">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-700 p-8 sm:p-14 text-center text-white shadow-2xl" data-testid="founder-channel">
            <FlaskConical className="h-10 w-10 mx-auto mb-4 opacity-90" />
            <Badge className="bg-white/15 text-white border-white/20 mb-4 hover:bg-white/15">EARLY ACCESS · DIRECT TO FOUNDER</Badge>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">Are you a teacher? Help us build the tool you actually need.</h2>
            <p className="mt-3 sm:mt-4 text-white/90 max-w-2xl mx-auto text-sm sm:text-base">
              We're building ActanonEdu in public, with teachers — not in a Bangalore boardroom. Sign up, try it, and tell the founder directly what works, what doesn't, and what's missing. Every message is read. Many become features.
            </p>
            <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3">
              <Button size="lg" onClick={goSignup} className="bg-white text-emerald-700 hover:bg-slate-100 text-sm sm:text-base h-12 px-6 sm:px-8 w-full sm:w-auto" data-testid="founder-signup-cta">
                Try Free · Then Tell the Founder <ArrowRight className="h-4 w-4 ml-1.5" />
              </Button>
              <Button size="lg" onClick={() => onFounderFeedback('founder-section')} variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 text-sm sm:text-base h-12 px-6 sm:px-7 w-full sm:w-auto" data-testid="founder-direct-wa">
                <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp the Founder
              </Button>
            </div>
            <div className="text-xs text-white/70 mt-5">Already trying ActanonEdu? <button onClick={goLogin} className="underline">Teacher Login</button></div>
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="py-12 sm:py-16 px-4 sm:px-8">
          <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 sm:p-14 text-center text-white shadow-2xl">
            <Brain className="h-10 w-10 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight">Stop building question papers from scratch.</h2>
            <p className="mt-3 text-white/90 max-w-xl mx-auto text-sm sm:text-base">Join thousands of Indian teachers already saving 10+ hours a week with ActanonEdu.</p>
            <Button size="lg" onClick={() => goSignupPromo('final_cta')} className="mt-6 sm:mt-7 bg-white text-indigo-700 hover:bg-slate-100 text-sm sm:text-base h-12 px-6 sm:px-8 w-full sm:w-auto" data-testid="final-cta-signup">
              {signupButtonLabel || 'Sign up Free'} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <div className="text-xs text-white/70 mt-4">Already a member? <button onClick={goLogin} className="underline">Teacher Login</button></div>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        <footer className="border-t border-slate-200 py-8 px-4 sm:px-8 text-xs sm:text-sm text-slate-500">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center gap-3 justify-between">
            <div>© {new Date().getFullYear()} ActanonEdu. All rights reserved.</div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <button onClick={openTerms} className="hover:text-slate-900" data-testid="footer-pilot-terms">Pilot Terms</button>
              <button onClick={openLegal} className="hover:text-slate-900" data-testid="footer-legal-disclaimer">Legal Disclaimer</button>
              <button onClick={openTakedown} className="hover:text-slate-900" data-testid="footer-notice-takedown">Notice &amp; Takedown</button>
              <button onClick={() => onFounderFeedback('footer')} className="hover:text-slate-900">Talk to Founder</button>
              <button onClick={goLogin} className="hover:text-slate-900">Teacher Login</button>
            </div>
          </div>
        </footer>
      </main>

      {/* ─── FLOATING WHATSAPP CTA ─── */}
      <button
        onClick={() => onWhatsApp('floating-cta', FOUNDER_MESSAGE)}
        className="fixed bottom-4 right-4 sm:bottom-5 sm:right-5 z-50 group rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-300/50 flex items-center gap-2.5 hover:scale-105 transition-transform p-3.5 sm:px-5 sm:py-3.5"
        data-testid="floating-cta"
        aria-label="Talk to Founder on WhatsApp about the School Pilot"
      >
        <MessageCircle className="h-5 w-5" />
        <div className="hidden sm:block text-left leading-tight">
          <div className="text-sm font-bold">School Pilot</div>
          <div className="text-[10px] opacity-90">Talk to Founder · 30 Days · Ends June 30</div>
        </div>
      </button>

      <PilotTermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
      <LegalDisclaimerModal open={legalOpen} onClose={() => setLegalOpen(false)} />
      <NoticeTakedownModal open={takedownOpen} onClose={() => setTakedownOpen(false)} />
    </div>
  );
}
